"use client";
import { useTranslations } from "next-intl";
import { Logger } from "@shared/utils/logger";
import { Waves, ChevronDown, Check } from "lucide-react";
import { JSX, useEffect, useMemo, useState } from "react";
import { isLowPowerDevice } from "@client/utils/client-utils";
import { useMediaDeviceSelect } from "@livekit/components-react";
import { useKrispNoiseFilter } from "@livekit/components-react/krisp";

/** Microphone settings - Toggle microphone and noise cancellation */
export const MicrophoneSettings = (): JSX.Element => {
  const t = useTranslations();
  const { isNoiseFilterEnabled, setNoiseFilterEnabled, isNoiseFilterPending } = useKrispNoiseFilter({
    filterOptions: {
      bufferOverflowMs: 100,
      bufferDropMs: 200,
      quality: isLowPowerDevice() ? "low" : "medium",
      onBufferDrop: () => {
        Logger.warn(
          "MicrophoneSettings",
          "krisp buffer dropped, noise filter versions >= 0.3.2 will automatically disable the filter"
        );
      }
    }
  });

  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind: "audioinput" });
  const [isDeviceMenuOpen, setIsDeviceMenuOpen] = useState(false);

  const activeDeviceLabel = useMemo(() => {
    const label = devices.find((d) => d.deviceId === activeDeviceId)?.label;
    return label === "Default" || label === "System Default"
      ? t("common.default")
      : label || t("settings.microphoneLabel");
  }, [devices, activeDeviceId, t]);

  useEffect(() => {
    setNoiseFilterEnabled(!isLowPowerDevice());
  }, [setNoiseFilterEnabled]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <section className="relative w-full">
        <button
          onClick={() => setIsDeviceMenuOpen(!isDeviceMenuOpen)}
          className={`w-full flex items-center justify-between p-3 rounded-xl bg-white border border-gray-200 text-gray-900 shadow-sm hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group ${
            isDeviceMenuOpen ? "ring-2 ring-primary/10 border-primary" : ""
          }`}
        >
          <span className="font-medium truncate max-w-[200px]" title={activeDeviceLabel}>
            {activeDeviceLabel}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${isDeviceMenuOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isDeviceMenuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsDeviceMenuOpen(false)} aria-hidden="true" />
            <div className="absolute top-full left-0 w-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
              <ul className="py-1 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                {devices.map((device) => (
                  <li key={device.deviceId}>
                    <button
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                        device.deviceId === activeDeviceId
                          ? "bg-primary/5 text-primary font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      onClick={() => {
                        setActiveMediaDevice(device.deviceId);
                        setIsDeviceMenuOpen(false);
                      }}
                    >
                      <span className="truncate pr-4">
                        {device.label === "Default" || device.label === "System Default"
                          ? t("common.default")
                          : device.label}
                      </span>
                      {device.deviceId === activeDeviceId && <Check className="w-4 h-4 shrink-0" />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </section>

      <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Waves className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-gray-900">{t("settings.noiseCancellation")}</span>
          </div>
          {isNoiseFilterPending && (
            <span className="text-xs text-gray-500 animate-pulse">{t("settings.updating")}</span>
          )}
        </div>

        <p className="text-xs text-gray-500 mb-4 leading-relaxed">{t("settings.noiseCancellationDesc")}</p>

        <button
          className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
            isNoiseFilterEnabled
              ? "bg-primary text-white shadow-md shadow-primary/20 hover:bg-primary-dark"
              : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300"
          }`}
          onClick={() => setNoiseFilterEnabled(!isNoiseFilterEnabled)}
          disabled={isNoiseFilterPending}
          aria-pressed={isNoiseFilterEnabled}
        >
          {isNoiseFilterEnabled ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span>{t("settings.enabled")}</span>
            </>
          ) : (
            t("settings.enableNoiseCancellation")
          )}
        </button>
      </div>
    </div>
  );
};
